import Home from "@/pages/home"
import { createFileRoute, notFound } from "@tanstack/react-router"
import { SITE_URL } from "@/lib/constants/site-url"
import { BAKERY_SCHEMA_ID, buildBakerySchema } from "@/lib/data/bakery-schema"
import { TOP_TOAST_ITEMS } from "@/lib/data/top-items"

const HERO_IMAGE = "/images/plusbake/optimized/hero-coffee-pastries-640.webp"
const HERO_IMAGE_SRCSET = [
  "/images/plusbake/optimized/hero-coffee-pastries-640.webp 640w",
  "/images/plusbake/optimized/hero-coffee-pastries-828.webp 828w",
  "/images/plusbake/optimized/hero-coffee-pastries-960.webp 960w",
  "/images/plusbake/optimized/hero-coffee-pastries-1280.webp 1280w",
].join(", ")
// Purpose-built 1200x630 landscape card so social scrapers (FB/X/LinkedIn/iMessage)
// render the share correctly. The portrait hero (1280x1920) was being declared as
// 1200x630, producing cropped/blank cards.
const OG_IMAGE = `${SITE_URL}/images/plusbake/optimized/og-home-1200x630.jpg`
const SUPPORTED_COUNTRY_CODES = new Set(["us"])

export const Route = createFileRoute("/$countryCode/")({
  loader: async ({ params }) => {
    const { countryCode } = params

    if (!SUPPORTED_COUNTRY_CODES.has(countryCode.toLowerCase())) {
      throw notFound()
    }

    return { countryCode }
  },
  head: () => {
    const title = "+Bake Cafe | Bakery, Coffee & Brunch in Rancho Cucamonga"
    const description =
      "Asian-fusion bakery cafe on Base Line Rd, Rancho Cucamonga. Salt bread, pandan and strawberry matcha lattes, rice bowls and Spam musubi. Open daily 7:30–3:30."
    const canonical = SITE_URL
    const businessId = BAKERY_SCHEMA_ID

    const localBusinessSchema = buildBakerySchema()

    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      name: "+Bake Cafe",
      alternateName: "Plus Bake Cafe",
      url: SITE_URL,
      publisher: { "@id": businessId },
    }

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: canonical },
      ],
    }

    const signatureItemsSchema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "+Bake Cafe top bakery and coffee items",
      itemListElement: TOP_TOAST_ITEMS.map(({ title }, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: title,
      })),
    }

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonical },
        { property: "og:image", content: OG_IMAGE },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:site_name", content: "+Bake Cafe" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: OG_IMAGE },
      ],
      links: [
        { rel: "canonical", href: canonical },
        {
          rel: "preload",
          href: HERO_IMAGE,
          as: "image",
          imageSrcSet: HERO_IMAGE_SRCSET,
          imageSizes: "(max-width: 1023px) 100vw, 50vw",
          fetchPriority: "high" as const,
        },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(localBusinessSchema),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(websiteSchema),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(breadcrumbSchema),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(signatureItemsSchema),
        },
      ],
    }
  },
  component: Home,
})
